import { Injectable, signal, inject } from '@angular/core';
import { SupabaseClient, User } from '@supabase/supabase-js';
import { createBrowserClient } from '@supabase/ssr';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly db: SupabaseClient;
  private readonly router = inject(Router);

  user    = signal<User | null>(null);
  loading = signal(true);

  constructor() {
    const DOMAIN = '.stonewavetech.com'
    this.db = createBrowserClient(
      environment.supabaseUrl,
      environment.supabaseAnonKey,
      {
        cookies: {
          getAll() {
            if (typeof document === 'undefined') return []
            return document.cookie.split('; ').filter(Boolean).map(c => {
              const idx = c.indexOf('=')
              return { name: c.slice(0, idx).trim(), value: c.slice(idx + 1) }
            })
          },
          setAll(cookiesToSet: Array<{ name: string; value: string; options?: Record<string, unknown> }>) {
            if (typeof document === 'undefined') return
            cookiesToSet.forEach(({ name, value, options }) => {
              const path = (options?.['path'] as string) ?? '/'
              document.cookie = `${name}=; path=${path}; Max-Age=0`
              let str = `${name}=${value}; domain=${DOMAIN}; path=${path}; SameSite=Lax; Secure`
              if (options?.['maxAge'] != null) str += `; Max-Age=${options['maxAge']}`
              document.cookie = str
            })
          },
        },
      }
    );

    this.db.auth.getSession().then(({ data: { session } }) => {
      this.user.set(session?.user ?? null);
      this.loading.set(false);
    });

    this.db.auth.onAuthStateChange((_event, session) => {
      this.user.set(session?.user ?? null);
    });
  }

  get client() { return this.db; }

  async signIn(email: string, password: string): Promise<string | null> {
    const { error } = await this.db.auth.signInWithPassword({ email, password });
    if (error) return 'Zugang verweigert. Prüfe deine Zugangsdaten.';

    const { data: isEmployee } = await this.db.rpc('is_employee');
    if (!isEmployee) {
      await this.db.auth.signOut();
      return 'Zugang verweigert. Dieser Account ist nicht für Prism freigeschaltet.';
    }
    return null;
  }

  async signOut() {
    await this.db.auth.signOut();
    this.router.navigate(['/login']);
  }

  async resetPassword(email: string): Promise<string | null> {
    const { error } = await this.db.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/set-password`,
    });
    return error ? error.message : null;
  }

  isAuthenticated(): boolean {
    return this.user() !== null;
  }

  async waitForInit(): Promise<void> {
    return new Promise(resolve => {
      const check = () => {
        if (!this.loading()) resolve();
        else setTimeout(check, 30);
      };
      check();
    });
  }
}
